/**
 * Design here: https://www.figma.com/file/urbIZ8Fnnln4Knx1lfo7mV/High-Level-Screens?type=design&node-id=5089-146902&mode=design&t=9HZlSXWSxwj25KKG-0
 */
import { FC } from 'react';

import { useIsTimelineCardShown } from 'features/deals/DealStages/useIsTimelineCardShown';
import {
  formatExecutionType,
  formatOriginationType,
  formatOwnership,
  formatPerformanceClass,
  formatRateBps,
  formatServicingPreference,
} from 'features/listings/termFormatters';
import { Tooltip } from 'react-tooltip';

import { gql } from '@apollo/client';

import { PricingType, TimelineStatus } from '__generated__/globalTypes';
import { StatusTrackerDeal } from './__generated__/StatusTracker';
import { formatCentsToDollars } from './BiddingAndPricing/formatting';
import { useDealRoomContext } from './DealRoomContext';
import { InfoBadge } from './InfoBadge';
import mainTaskComplete from '../../../assets/svgs/main-task-complete.svg';
import mainTaskNotStarted from '../../../assets/svgs/main-task-not-started.svg';
import mainTaskPending from '../../../assets/svgs/main-task-pending.svg';
import subTaskComplete from '../../../assets/svgs/subtask-complete.svg';

const StatusTrackerFragments = {
  deal: gql`
    fragment StatusTrackerDeal on Deal {
      id
      state {
        status
        label
        tooltip
        blocker
      }
      timeline {
        title
        subject
        status
        calls_to_action {
          title
          card {
            __typename
          }
        }
      }
      listing {
        id
        name
        __typename
        ... on PublicListing {
          execution_type
          servicing_preference
          is_servicing_negotiable
          servicing_fee_rate_bps
          minimum_carve_cents
          performance_class
          origination_type
          participation_rate
          is_participation_negotiable
          pricing_type
          pricing_rate_bps
        }
        ... on UserCompanyListing {
          execution_type
          servicingPref: servicing_preference # graphql feels weird about the names being same with different type
          isServicingNegotiable: is_servicing_negotiable
          servicingFeeRateBps: servicing_fee_rate_bps
          minimum_carve_cents
          performance_class
          origination_type
          participationRate: participation_rate
          isParticipationNegotiable: is_participation_negotiable
          pricingType: pricing_type
          pricingRateBps: pricing_rate_bps
        }
      }
    }
  `,
};

const TaskIcon = ({ image }: { image: string }) => (
  <div
    className="absolute -top-5px left-5px h-26px w-26px bg-background-canvas bg-center bg-no-repeat"
    style={{ backgroundImage: `url('${image}')` }}
  />
);

type StatusTrackerProps = {
  deal: StatusTrackerDeal | null;
};

const StatusTracker: FC<StatusTrackerProps> = (props: StatusTrackerProps) => {
  const { deal } = props;
  // TODO: use same query to render the cards and these subtasks, then useRef to scroll to the correct card
  const { selectCard } = useDealRoomContext();
  const isCardShown = useIsTimelineCardShown();

  const listing = deal?.listing;
  if (!listing) {
    return <></>;
  }

  const servicing =
    'servicing_preference' in listing ? listing.servicing_preference : listing.servicingPref;

  const isServicingNegotiable =
    'is_servicing_negotiable' in listing
      ? listing.is_servicing_negotiable
      : listing.isServicingNegotiable;

  const servicingFeeRateBps =
    'servicing_fee_rate_bps' in listing
      ? listing.servicing_fee_rate_bps
      : listing.servicingFeeRateBps;

  const participationRate =
    'participation_rate' in listing ? listing.participation_rate : listing.participationRate;

  const isParticipationNegotiable =
    'is_participation_negotiable' in listing
      ? listing.is_participation_negotiable
      : listing.isParticipationNegotiable;

  const pricingType = 'pricing_type' in listing ? listing.pricing_type : listing.pricingType;

  const pricingRateBps =
    'pricing_rate_bps' in listing ? listing.pricing_rate_bps : listing.pricingRateBps;

  const ownershipLabel = isParticipationNegotiable ? null : formatOwnership(participationRate);

  const calculateActionIndex = (stageIndex: number, callToActionIndex: number) => {
    let previousActionsCount = 0;
    for (let i = 0; i < stageIndex; i++) {
      previousActionsCount += deal?.timeline[i].calls_to_action.length || 0;
    }

    return previousActionsCount + callToActionIndex;
  };

  return (
    <div className="relative h-full overflow-auto border border-solid border-[#2b2b2b] bg-background-canvas p-[2px] px-5 pb-5 pt-[18px]">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 break-all text-lg text-foreground-default">
            {listing.name}
          </div>
          <span className="shrink-0 text-sm text-green-400" data-tooltip-id={deal?.listing.name}>
            {deal?.state.label}
          </span>
        </div>
        {(listing.__typename === 'PublicListing' ||
          listing.__typename === 'UserCompanyListing') && (
          <div className="flex flex-col">
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                <div className="text-xs text-label-muted">Origination Type</div>
                <InfoBadge label={formatOriginationType(listing.origination_type)} />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                <div className="text-xs text-label-muted">Execution</div>
                <InfoBadge label={formatExecutionType(listing.execution_type)} />
              </div>
            </div>
            {servicing && (
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                  <div className="text-xs text-label-muted">Servicing</div>
                  <InfoBadge label={formatServicingPreference(servicing)} />
                </div>
              </div>
            )}
            {!isServicingNegotiable && servicingFeeRateBps != null && (
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                  <div className="text-xs text-label-muted">Servicing Fee</div>
                  <InfoBadge label={formatRateBps(servicingFeeRateBps)} />
                </div>
              </div>
            )}
            {ownershipLabel && (
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                  <div className="text-xs text-label-muted">Ownership</div>
                  <InfoBadge label={ownershipLabel} />
                </div>
              </div>
            )}
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                <div className="text-xs text-label-muted">Performance</div>
                <InfoBadge label={formatPerformanceClass(listing.performance_class)} />
              </div>
            </div>
            {pricingType === PricingType.PUBLISHED && pricingRateBps != null && (
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                  <div className="text-xs text-label-muted">Published Price</div>
                  <InfoBadge label={formatRateBps(pricingRateBps)} />
                </div>
              </div>
            )}
            {listing.minimum_carve_cents ? (
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between border-b border-b-slate-500 px-2 py-1">
                  <div className="text-xs text-label-muted">Minimum Carve</div>
                  <InfoBadge label={formatCentsToDollars(listing.minimum_carve_cents)} />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="relative overflow-auto">
        <div className="absolute bottom-0 left-[17px] top-4 w-[1px] bg-[#2b2b2b]" />
        {deal?.timeline.map((stage, stageIndex) => {
          return (
            <div key={`${stage.title}-${stageIndex}`}>
              <div className="relative my-5 pl-10">
                {stage.status === TimelineStatus.COMPLETE ? (
                  <TaskIcon image={mainTaskComplete} />
                ) : stage.status === TimelineStatus.PENDING ? (
                  <TaskIcon image={mainTaskNotStarted} />
                ) : (
                  <TaskIcon image={mainTaskPending} />
                )}
                <div>
                  <span className="text-sm text-label-muted">{stage.title}</span>
                  {stage.status && stage.status !== TimelineStatus.PENDING && (
                    <span className="ml-5 border-l-1.5px border-warning-default pl-1 font-pt-sans-narrow text-xs font-normal text-warning-default">
                      {stage.status}
                    </span>
                  )}
                </div>
                <div className="text-xs text-label-muted">{stage.subject}</div>
              </div>
              {stage.calls_to_action?.map((cta, callToActionIndex) => {
                if (!isCardShown(cta.card)) return null;
                return (
                  <div
                    key={`${cta.title}-${stageIndex + callToActionIndex}`}
                    className="relative my-5 pl-10 text-xs text-slate-200"
                  >
                    <div
                      className="absolute left-3 top-px h-2.5 w-2.5 bg-background-canvas bg-center bg-no-repeat"
                      style={{ backgroundImage: `url("${subTaskComplete}")` }}
                    />
                    <span
                      onClick={() => {
                        const actionIndex = calculateActionIndex(stageIndex, callToActionIndex);
                        if (cta.card.__typename !== 'PlaceholderCard') {
                          const ctaKey =
                            cta.title === 'Bidding and Pricing'
                              ? cta.title
                              : `${cta.title}-${actionIndex}`;

                          selectCard(ctaKey);
                        }
                      }}
                      className={`underline ${cta.card.__typename === 'PlaceholderCard' ? 'not-allowed cursor-not-allowed opacity-50' : 'pointer cursor-pointer'}`}
                    >
                      {cta.title}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <Tooltip id={deal?.listing.name} className="max-w-full">
        {deal?.state.tooltip}
      </Tooltip>
    </div>
  );
};

export default StatusTracker;
export { StatusTrackerFragments };
